<script lang="ts">
import PolicyDocumentUploadListModal from '@/views/account/client_readiness/PolicyDocumentUploadListModal.vue';
import PublishDocumentListModal from '@/views/account/client_readiness/PublishDocumentListModal.vue';
import AppModal from '@/components/v2/AppModal.vue';
import { ExternalUser } from '@/types/User';
import { AccountServicingRole } from '@/types/AccountSummary';

export default {
  name: 'AccountClientReadiness',
  components: {
    PolicyDocumentUploadListModal,
    PublishDocumentListModal,
    AppModal,
  },
};

interface ExternalUserDisplay extends ExternalUser {
  relativeLastActive: string;
}

// tooltips
const warningToolTip = 'Needs Attention';
const policiesWithNoBinderTooltip = 'Policies without an uploaded binder document. To upload a binder, attach the file in Epic.';
const policiesWithNoPolicyDocumentTooltip =
  'Policies without an uploaded copy of the policy document. To upload a policy, attach the file in Epic or visit the Documents tab.';
const policiesWithUnpublishedBinderTooltip =
  'Policies that have an uploaded binder that is not published for a client to view. Visit the Documents tab to publish these binders.';
const policiesWithUnpublishedPolicyDocumentTooltip =
  'Policies that have an uploaded policy document that is not published for a client to view. Visit the Documents tab to publish these policies.';
const policiesWithPossibleInvalidPolicyNumberTooltip =
  'Policies with a possibly invalid policy number, such as "TBD", blank, or more than 25 characters. These policy numbers must be changed in Epic. The new policy number will appear on the Platform after the data refreshes overnight.';
const policiesWithNoPremiumOrFeeTooltip =
  'Policies without associated premiums for fees. Premium and fee information can be updated in Epic, and changes will be reflected on the Platform after the data refreshes overnight.';
const policiesWithInvalidDatesTooltip = 'Policies with suspicious effective or expiration dates, such as single-day coverage.';
const bindersWithoutValidatedResultsTooltip =
  'Binders that have been processed by AI extraction but have not yet been reviewed and validated. Click to view the list and navigate to the AI Data Extraction Results page to validate.';
</script>

<script setup lang="ts">
import { accountService } from '@/services/api';
import { computed, onBeforeMount, ref, watch, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { relativeDateTime } from '@/utilities/Dates';
import { Account } from '@/types/Account';
import { CheckCircle2, AlertTriangle, PencilIcon as Pencil, FileUp, Info, Users, ExternalLink, ListChecks } from '@lucide/vue';
import { OwnershipType } from '@/types/StockExchange';
import clientReadinessService, {
  DocumentsKPI,
  PoliciesKPI,
  RuleViolatingPolicy,
  RuleViolatingProgram,
  UnvalidatedBinder,
} from '@/services/v2/ClientReadinessService';
import { addCommas } from '@/utilities/Number';
import EditPolicyModal from '@/components/EditPolicyModal.vue';
import PoliciesCollapseView from '@/components/v2/client_readiness/PoliciesCollapseView.vue';
import ProgramsCollapseView from '@/components/v2/client_readiness/ProgramsCollapseView.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { formatCurrency } from '@/utilities/Money';
import { externalPolicyName } from '@/utilities/OrgConfig';

const props = defineProps<{
  account: Account;
}>();

const router = useRouter();

// -----
// refs
// -----

const showNonCAC = ref(false);
const showCurrentOnly = ref(true);

const editPolicy = reactive({
  show: false,
  policyId: 0,
  mainPolicyNumber: '',
});

const externalUsers = ref<ExternalUserDisplay[]>([]);
const accountServicers = ref<AccountServicingRole[]>([]);

const documentsKPI = ref<DocumentsKPI>({
  policiesWithNoBinderCount: 0,
  policiesWithNoPolicyDocumentCount: 0,
  unpublishedBinderCount: 0,
  unpublishedPolicyDocumentCount: 0,
  unvalidatedBinderCount: 0,
});

const policiesKPI = ref<PoliciesKPI>({
  policiesWithNoPremiumOrFeesCount: 0,
  policiesWithInvalidDatesCount: 0,
  policiesWithPossibleInvalidPolicyNumberCount: 0,
  totalPolicyCount: 0,
});

const documentsKPICache = ref<Map<string, DocumentsKPI>>(new Map<string, DocumentsKPI>());
const policiesKPICache = ref<Map<string, PoliciesKPI>>(new Map<string, PoliciesKPI>());

const showPolicyDocumentUploadListModal = ref(false);
const showBinderUploadListModal = ref(false);
const showInvalidPolicyNumberModal = ref(false);
const showInvalidMissingPremiumOrFees = ref(false);
const showInvalidDates = ref(false);

const showBinderPublishModal = ref(false);
const showPolicyPublishModal = ref(false);
const showUnvalidatedBindersModal = ref(false);

const unvalidatedBinders = ref<UnvalidatedBinder[]>([]);

const ruleViolatingPolicies = ref<RuleViolatingPolicy[]>([]);
const ruleViolatingPrograms = ref<RuleViolatingProgram[]>([]);

const ruleViolatingPoliciesCache = ref<Map<string, RuleViolatingPolicy[]>>(new Map<string, RuleViolatingPolicy[]>());
const ruleViolatingProgramsCache = ref<Map<string, RuleViolatingProgram[]>>(new Map<string, RuleViolatingProgram[]>());

// ---------
// computed
// ---------

const accountId = computed(() => useRoute().params.id as string);
const ownershipType = computed(() => props.account.ownershipType ?? '--');
const cacIndustryName = computed(() => props.account.industryName ?? '--');
const sicIndustryName = computed(() => props.account.sicDescription ?? '--');

const stockTicker = computed(() => {
  if (!props.account.stockTicker || !props.account.stockExchange) {
    return '--';
  }
  return props.account.stockTicker;
});

const stockExchange = computed(() => {
  return props.account.stockExchange;
});

const website = computed(() => {
  if (props.account.noDomain) {
    return 'Website does not exist';
  }
  return props.account.domainName ?? '--';
});

const missingOwnershipType = computed(() => !props.account.ownershipType);
const missingCacIndustryName = computed(() => !props.account.industryName);
const missingSicIndustryName = computed(() => !props.account.sicDescription);
const missingStockTicker = computed(() => !props.account.stockTicker || !props.account.stockExchange);
const missingWebSite = computed(() => {
  // if true, then user explicitly set no website, so it's not missing
  if (props.account.noDomain) {
    return false;
  }
  return !props.account.domainName;
});

const hasPoliciesWithoutPolicyDocument = computed(() => documentsKPI.value.policiesWithNoPolicyDocumentCount > 0);
const hasPoliciesWithoutBinder = computed(() => documentsKPI.value.policiesWithNoBinderCount > 0);

const hasUnpublishedBinders = computed(() => documentsKPI.value.unpublishedBinderCount > 0);
const hasUnpublishedPolicyDocuments = computed(() => documentsKPI.value.unpublishedPolicyDocumentCount > 0);
const hasUnvalidatedBinders = computed(() => documentsKPI.value.unvalidatedBinderCount > 0);

const policiesWithoutLimit = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.noLimitRule));
const policiesWithoutRetention = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.invalidRetentionRule));
const policiesWithoutAttachmentPoint = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.invalidAttachmentPointRule));
const programsWithNoServicingRoles = computed(() => ruleViolatingPrograms.value.filter((program) => program.noServicingRolesRule));
const policiesWithInvalidPolicyNumber = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.invalidPolicyNumber));
const policiesWithMissingPremiumOrFees = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.invalidMissingPremiumOrFees));
const policiesWithInvalidDates = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.invalidDates));

const servicersByRole = computed(() => {
  const grouped = new Map<string, AccountServicingRole[]>();

  accountServicers.value.forEach((servicer) => {
    if (!grouped.has(servicer.servicingRoleTitle)) {
      grouped.set(servicer.servicingRoleTitle, []);
    }
    grouped.get(servicer.servicingRoleTitle)?.push(servicer);
  });

  return grouped;
});

// -----------
// lifecycles
// -----------

onBeforeMount(async () => {
  await Promise.all([fetchExternalUsers(), fetchAccountServicers(), fetchKPIs(), fetchRuleViolatingPolicies(), fetchRuleViolatingPrograms()]);
});

// ---------
// watchers
// ---------

watch([showNonCAC, showCurrentOnly], async () => {
  await Promise.all([fetchDocumentsKPI(), fetchPoliciesKPI(), fetchRuleViolatingPolicies(), fetchRuleViolatingPrograms()]);
});

// ----------
// functions
// ----------

async function fetchExternalUsers(): Promise<void> {
  externalUsers.value = (await accountService.getExternalUsersForAccount(accountId.value)).map((user) => {
    if (!user.lastActive) return { ...user, lastActive: undefined } as ExternalUserDisplay;

    return {
      ...user,
      lastActive: new Date(user.lastActive).toLocaleDateString(undefined, { dateStyle: 'short' }),
      relativeLastActive: relativeDateTime(user.lastActive),
    } as ExternalUserDisplay;
  });
}

async function fetchAccountServicers(): Promise<void> {
  accountServicers.value = await accountService.getAccountServicers(accountId.value);
}

async function fetchKPIs(): Promise<void> {
  await Promise.all([fetchDocumentsKPI(), fetchPoliciesKPI()]);
}

async function fetchDocumentsKPI(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (documentsKPICache.value.has(JSON.stringify(queryParams))) {
    documentsKPI.value = documentsKPICache.value.get(JSON.stringify(queryParams)) as DocumentsKPI;
  } else {
    documentsKPI.value = await clientReadinessService.getDocumentsKPI(Number(accountId.value), queryParams);
    documentsKPICache.value.set(JSON.stringify(queryParams), documentsKPI.value);
  }
}

async function fetchPoliciesKPI(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (policiesKPICache.value.has(JSON.stringify(queryParams))) {
    policiesKPI.value = policiesKPICache.value.get(JSON.stringify(queryParams)) as PoliciesKPI;
  } else {
    policiesKPI.value = await clientReadinessService.getPoliciesKPI(Number(accountId.value), queryParams);
    policiesKPICache.value.set(JSON.stringify(queryParams), policiesKPI.value);
  }
}

async function fetchRuleViolatingPolicies(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (ruleViolatingPoliciesCache.value.has(JSON.stringify(queryParams))) {
    ruleViolatingPolicies.value = ruleViolatingPoliciesCache.value.get(JSON.stringify(queryParams)) as RuleViolatingPolicy[];
  } else {
    ruleViolatingPolicies.value = await clientReadinessService.getRuleViolatingPolicies(Number(accountId.value), queryParams);
    ruleViolatingPoliciesCache.value.set(JSON.stringify(queryParams), ruleViolatingPolicies.value);
  }
}

async function fetchRuleViolatingPrograms(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (ruleViolatingProgramsCache.value.has(JSON.stringify(queryParams))) {
    ruleViolatingPrograms.value = ruleViolatingProgramsCache.value.get(JSON.stringify(queryParams)) as RuleViolatingProgram[];
  } else {
    ruleViolatingPrograms.value = await clientReadinessService.getRuleViolatingPrograms(Number(accountId.value), queryParams);
    ruleViolatingProgramsCache.value.set(JSON.stringify(queryParams), ruleViolatingPrograms.value);
  }
}

async function handleShowUnvalidatedBinders() {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };
  unvalidatedBinders.value = await clientReadinessService.getUnvalidatedBinders(Number(accountId.value), queryParams);
  showUnvalidatedBindersModal.value = true;
}

function handleEditAccount() {
  router.push(`/account/${accountId.value}/edit`);
}

async function handleDocumentUploaded() {
  documentsKPICache.value.clear();
  await fetchDocumentsKPI();
}

async function handleDocumentsPublished() {
  documentsKPICache.value.clear();
  await fetchDocumentsKPI();
}

function handleEditPolicy(policyId: number, mainPolicyNumber: string) {
  editPolicy.policyId = policyId;
  editPolicy.mainPolicyNumber = mainPolicyNumber;
  editPolicy.show = true;
}

function handlePolicySave() {
  editPolicy.show = false;
  ruleViolatingPoliciesCache.value.clear();
  fetchRuleViolatingPolicies();
}

async function handleToggleServicerPublished(servicer: AccountServicingRole) {
  try {
    const newPublishedState = !servicer.isPublishedOnSomePolicies;
    await accountService.toggleServicerPublished(accountId.value, servicer.name, servicer.servicingRoleTitle, newPublishedState);
    // Refetch the servicers to get the updated state
    await fetchAccountServicers();
  } catch (error) {
    console.error('Failed to toggle servicer published status:', error);
    // Optionally show a toast/notification to the user
  }
}
</script>

<template>
  <div class="mx-auto flex max-w-7xl flex-col items-center px-2">
    <div class="mt-2 flex w-full flex-row">
      <AppButton
        class="mr-0 ml-auto"
        data-testid="launch-data-readiness-wizard"
        @click="$router.push({ name: 'account-data-readiness-wizard', params: { id: accountId } })">
        <ListChecks :size="16" />
        Review Client Readiness
      </AppButton>
    </div>
    <!-- KPI panel -->
    <div class="mt-2 w-full flex-1 rounded-lg border border-solid border-base-300 p-4 pt-0 shadow-sm">
      <div class="flex w-full items-center justify-between gap-2 py-1">
        <div class="mr-0 ml-auto flex gap-2">
          <label class="label cursor-pointer gap-2">
            <span class="label-text text-sm transition-all">Show Current Only</span>
            <input
              type="checkbox"
              v-model="showCurrentOnly"
              class="toggle border-solid toggle-primary toggle-sm" />
          </label>
          <label class="label cursor-pointer gap-2">
            <span class="label-text text-sm transition-all">Include {{ externalPolicyName() }}</span>
            <input
              type="checkbox"
              v-model="showNonCAC"
              class="toggle border-solid toggle-primary toggle-sm" />
          </label>
        </div>
      </div>

      <!-- POLICIES KPI -->
      <div class="grid w-full max-w-7xl flex-1 grid-cols-2 gap-4 lg:grid-cols-8">
        <div class="col-span-8 space-y-4">
          <div class="card-border card relative rounded-lg">
            <div class="absolute -top-2 flex w-full justify-center">
              <h4 class="bg-white px-2 text-center text-sm text-gray-500 uppercase">Policies ({{ policiesKPI.totalPolicyCount }} Total)</h4>
            </div>
            <div class="stats stats-vertical grid-cols-none gap-1 overflow-visible lg:grid-cols-8">
              <div class="stat rounded-t-lg shadow-sm lg:rounded-tl-lg lg:rounded-tr-none lg:rounded-br-none lg:rounded-bl-lg">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(documentsKPI.policiesWithNoBinderCount) }}</span>
                  <div
                    v-if="hasPoliciesWithoutBinder"
                    class="tooltip"
                    data-tip="Attach Binders">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showBinderUploadListModal = true">
                      <FileUp :size="20" />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">No Binder Attached</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:tooltip-right lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithNoBinderTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(documentsKPI.policiesWithNoPolicyDocumentCount) }}</span>
                  <div
                    v-if="hasPoliciesWithoutPolicyDocument"
                    class="tooltip"
                    data-tip="Attach Policy Documents">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showPolicyDocumentUploadListModal = true">
                      <FileUp :size="20" />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">No Policy Attached</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:tooltip-right lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithNoPolicyDocumentTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(documentsKPI.unpublishedBinderCount) }}</span>
                  <div
                    v-if="hasUnpublishedBinders"
                    class="tooltip"
                    data-tip="Publish Binders">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showBinderPublishModal = true">
                      <Users :size="20" />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">No Binder Published</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:tooltip-right lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithUnpublishedBinderTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(documentsKPI.unvalidatedBinderCount) }}</span>
                  <div
                    v-if="hasUnvalidatedBinders"
                    class="tooltip"
                    data-tip="View Unvalidated Binders">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="handleShowUnvalidatedBinders">
                      <Info />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">Unvalidated Binders</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:-top-10 lg:-left-20"
                  :data-tip="bindersWithoutValidatedResultsTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(documentsKPI.unpublishedPolicyDocumentCount) }}</span>
                  <div
                    v-if="hasUnpublishedPolicyDocuments"
                    class="tooltip"
                    data-tip="Publish Policy Documents">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showPolicyPublishModal = true">
                      <Users :size="20" />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">No Policy Published</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:tooltip-right lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithUnpublishedPolicyDocumentTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(policiesKPI.policiesWithPossibleInvalidPolicyNumberCount) }}</span>
                  <div
                    v-if="policiesKPI.policiesWithPossibleInvalidPolicyNumberCount > 0"
                    class="tooltip"
                    data-tip="View Policies">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showInvalidPolicyNumberModal = true">
                      <Info />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">Invalid Policy Number</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:tooltip-right lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithPossibleInvalidPolicyNumberTooltip" />
              </div>
              <div class="stat shadow-sm">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(policiesKPI.policiesWithNoPremiumOrFeesCount) }}</span>
                  <div
                    v-if="policiesKPI.policiesWithNoPremiumOrFeesCount > 0"
                    class="tooltip"
                    data-tip="View Policies">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showInvalidMissingPremiumOrFees = true">
                      <Info />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">No Premium or Fee</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithNoPremiumOrFeeTooltip" />
              </div>
              <div class="stat rounded-b-lg shadow-sm lg:rounded-tl-none lg:rounded-tr-lg lg:rounded-br-lg lg:rounded-bl-none">
                <div class="peer stat-value flex w-fit items-center gap-2">
                  <span>{{ addCommas(policiesKPI.policiesWithInvalidDatesCount) }}</span>
                  <div
                    v-if="policiesKPI.policiesWithInvalidDatesCount > 0"
                    class="tooltip"
                    data-tip="View Policies">
                    <button
                      class="text-sm text-gray-500 hover:text-secondary"
                      @click="showInvalidDates = true">
                      <Info />
                    </button>
                  </div>
                </div>
                <div class="stat-desc text-gray-800">Invalid Dates</div>
                <div
                  class="peer-hover:tooltip-open tooltip lg:-top-10 lg:-left-20"
                  :data-tip="policiesWithInvalidDatesTooltip" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="grid w-full max-w-7xl flex-1 grid-cols-2 gap-4 pt-8 md:grid-cols-9">
      <!-- Account Details -->
      <div class="col-span-3 flex flex-col gap-4">
        <div class="card-border card rounded-lg shadow-sm">
          <div class="card-body p-4">
            <div class="pb-2">
              <h2 class="card-title justify-between">
                <div>Account Details</div>
                <button
                  @click="handleEditAccount"
                  class="btn btn-sm hover:bg-secondary hover:text-base-200">
                  <Pencil :size="13" />
                </button>
              </h2>
            </div>
            <div class="flex flex-col gap-4">
              <div class="flex items-center justify-between rounded-sm p-4 shadow-sm">
                <div>
                  <div class="text-base text-gray-500">CAC Industry</div>
                  <div
                    class="text-lg"
                    :class="{ 'text-red-500': missingCacIndustryName }">
                    {{ cacIndustryName }}
                  </div>
                </div>
                <CheckCircle2
                  v-if="!missingCacIndustryName"
                  class="text-green-500" />
                <div
                  v-else
                  class="tooltip"
                  :data-tip="warningToolTip">
                  <AlertTriangle class="text-red-500" />
                </div>
              </div>
              <div class="flex items-center justify-between rounded-sm p-4 shadow-sm">
                <div>
                  <div class="text-base text-gray-500">SIC Industry</div>
                  <div
                    class="text-lg"
                    :class="{ 'text-red-500': missingSicIndustryName }">
                    {{ sicIndustryName }}
                  </div>
                </div>
                <CheckCircle2
                  v-if="!missingSicIndustryName"
                  class="text-green-500" />
                <div
                  v-else
                  class="tooltip"
                  :data-tip="warningToolTip">
                  <AlertTriangle class="text-red-500" />
                </div>
              </div>
              <div class="flex items-center justify-between rounded-sm p-4 shadow-sm">
                <div>
                  <div class="text-base text-gray-500">Ownership Type</div>
                  <div
                    class="text-lg"
                    :class="{ 'text-red-500': missingOwnershipType }">
                    {{ ownershipType }}
                  </div>
                </div>
                <CheckCircle2
                  v-if="!missingOwnershipType"
                  class="text-green-500" />
                <div
                  v-else
                  class="tooltip"
                  :data-tip="warningToolTip">
                  <AlertTriangle class="text-red-500" />
                </div>
              </div>
              <div
                v-if="!missingOwnershipType && ownershipType === OwnershipType.Public"
                class="flex items-center justify-between rounded-sm p-4 shadow-sm">
                <div>
                  <div class="text-base text-gray-500">Stock Ticker</div>
                  <div
                    class="text-lg"
                    :class="{ 'text-red-500': missingStockTicker }">
                    {{ stockTicker }}
                  </div>
                  <div
                    v-if="!missingStockTicker"
                    class="text-sm text-gray-500">
                    {{ stockExchange }}
                  </div>
                </div>
                <CheckCircle2
                  v-if="!missingStockTicker"
                  class="text-green-500" />
                <div
                  v-else
                  class="tooltip"
                  :data-tip="warningToolTip">
                  <AlertTriangle class="text-red-500" />
                </div>
              </div>
              <div class="flex items-center justify-between rounded-sm p-4 shadow-sm">
                <div>
                  <div class="text-base text-gray-500">Website</div>
                  <div
                    class="text-lg"
                    :class="{ 'text-red-500': missingWebSite }">
                    {{ website }}
                  </div>
                </div>
                <CheckCircle2
                  v-if="!missingWebSite"
                  class="text-green-500" />
                <div
                  v-else
                  class="tooltip"
                  :data-tip="warningToolTip">
                  <AlertTriangle class="text-red-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card-border card rounded-lg border-solid shadow-sm">
          <div class="card-body p-4">
            <h2 class="card-title pb-2">External Users</h2>
            <table
              v-if="externalUsers.length > 0"
              class="apptable dense --tight-first">
              <thead>
                <tr>
                  <td>User</td>
                  <td>Email</td>
                  <td>Last Login</td>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="user in externalUsers"
                  :key="user.id">
                  <td>{{ user.name }}</td>
                  <td class="break-all">{{ user.email }}</td>
                  <td>
                    <div>{{ user.lastActive }}</div>
                    <div class="text-sm text-gray-600">{{ user.relativeLastActive }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else
              class="flex justify-center">
              <div class="alert flex w-96 justify-center bg-gray-100">No external users have been added to this account.</div>
            </div>
          </div>
        </div>
        <div class="card-border card rounded-lg border-solid shadow-sm">
          <div class="card-body p-4">
            <h2 class="card-title pb-2">Account Service Personnel</h2>
            <div
              v-if="accountServicers.length > 0"
              class="flex flex-col gap-4">
              <div
                v-for="[roleTitle, servicers] in servicersByRole"
                :key="roleTitle"
                class="rounded-sm shadow-sm">
                <div class="bg-gray-50 p-2 text-sm font-semibold text-gray-700">
                  {{ roleTitle }}
                </div>
                <div class="divide-y">
                  <div
                    v-for="servicer in servicers"
                    :key="`${servicer.name}-${servicer.servicingRoleTitle}`"
                    class="flex items-center justify-between p-3">
                    <div class="text-base">{{ servicer.name }}</div>
                    <div class="flex items-center gap-2">
                      <div
                        v-if="servicer.isPublishedOnSomePolicies && servicer.isUnpublishedOnSomePolicies"
                        class="tooltip tooltip-left"
                        data-tip="Servicer is hidden on some policies">
                        <Info
                          :size="16"
                          class="text-gray-500" />
                      </div>

                      <label class="label cursor-pointer gap-2">
                        <span class="label-text text-sm">Client Visible</span>
                        <input
                          type="checkbox"
                          :checked="servicer.isPublishedOnSomePolicies"
                          @change="handleToggleServicerPublished(servicer)"
                          class="toggle border-solid toggle-primary toggle-sm" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex justify-center">
              <div class="alert flex w-96 justify-center bg-gray-100">No servicers have been assigned to this account.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rule Violation -->
      <div class="card-border card col-span-6 row-span-2 self-start rounded-lg shadow-sm">
        <div class="card-body p-4">
          <h2 class="card-title pb-2">Rule Violations</h2>
          <PoliciesCollapseView
            title="Policies Without a Limit"
            :policies="policiesWithoutLimit"
            @editPolicy="handleEditPolicy" />
          <PoliciesCollapseView
            title="Primary Policies Without a Retention"
            :policies="policiesWithoutRetention"
            @editPolicy="handleEditPolicy" />
          <PoliciesCollapseView
            title="Excess Policies Without an Attachment Point"
            :policies="policiesWithoutAttachmentPoint"
            @editPolicy="handleEditPolicy" />
          <ProgramsCollapseView
            title="Programs Without Servicing Roles"
            :programs="programsWithNoServicingRoles"
            :actions="[]" />
        </div>
      </div>
    </div>

    <PolicyDocumentUploadListModal
      :accountId="Number(accountId)"
      :ShowNonCAC="showNonCAC"
      :ShowCurrentOnly="showCurrentOnly"
      doc-type="Policy"
      title="Upload Policy Documents"
      v-if="showPolicyDocumentUploadListModal"
      @close="showPolicyDocumentUploadListModal = false"
      @document-uploaded="handleDocumentUploaded" />

    <PolicyDocumentUploadListModal
      :accountId="Number(accountId)"
      :ShowNonCAC="showNonCAC"
      :ShowCurrentOnly="showCurrentOnly"
      doc-type="Binder"
      title="Upload Binders"
      v-if="showBinderUploadListModal"
      @close="showBinderUploadListModal = false"
      @document-uploaded="handleDocumentUploaded" />

    <PublishDocumentListModal
      v-if="showBinderPublishModal"
      :accountId="Number(accountId)"
      doc-type="Binder"
      title="Publish Binders"
      @close="showBinderPublishModal = false"
      @documents-published="handleDocumentsPublished" />

    <PublishDocumentListModal
      v-if="showPolicyPublishModal"
      :accountId="Number(accountId)"
      doc-type="Policy"
      title="Publish Policy Documents"
      @close="showPolicyPublishModal = false"
      @documents-published="handleDocumentsPublished" />

    <EditPolicyModal
      :show="editPolicy.show"
      :policy-id="editPolicy.policyId"
      :policy-number="editPolicy.mainPolicyNumber"
      @close="
        console.log('test');
        editPolicy.show = false;
      "
      @save="handlePolicySave" />

    <AppModal
      :show="showInvalidPolicyNumberModal"
      title="Policies with Invalid Policy Number"
      size="lg"
      action-button-label="Close"
      @close="showInvalidPolicyNumberModal = false"
      :handle-cancel="() => (showInvalidPolicyNumberModal = false)">
      <template #content>
        <div class="mb-4">
          <h3>Selection Criteria</h3>
          <p>
            Policy Numbers that are blank, &ldquo;TBD&rdquo;, &ldquo;Do Not Use&rdquo;, 25 characters or more, or the name of the Insurer. To fix this
            issue, update the Policy Number in Epic, and it will updated on the Platform overnight.
          </p>
        </div>

        <table class="apptable dense --tight-first text-sm">
          <thead>
            <tr>
              <td class="nosort">Policy Description</td>
              <td class="nosort">Policy Number</td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="policy in policiesWithInvalidPolicyNumber"
              :key="policy.policyId">
              <td>{{ policy.description }}</td>
              <td>
                <span v-if="policy.mainPolicyNumber">{{ policy.mainPolicyNumber }}</span>
                <span
                  v-else
                  class="text-gray-400"
                  >&lt;blank&gt;</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <template #actions>
        <AppButton
          primary
          @click="showInvalidPolicyNumberModal = false">
          Done
        </AppButton>
      </template>
    </AppModal>

    <AppModal
      :show="showInvalidMissingPremiumOrFees"
      title="Policies with No Premium or Fee"
      size="lg"
      action-button-label="Close"
      @close="showInvalidMissingPremiumOrFees = false"
      :handle-cancel="() => (showInvalidMissingPremiumOrFees = false)">
      <template #content>
        <div class="mb-4">
          <h3>Selection Criteria</h3>
          <p>
            Policies missing both Premium and Fees. For direct-bill policies, enter the Estimated Premium into Epic and it will appear overnight.
            Agency-bill policies will update once transacted in Epic.
          </p>
        </div>

        <table class="apptable dense --tight-first text-sm">
          <thead>
            <tr>
              <td class="nosort">Description</td>
              <td class="nosort">Policy Number</td>
              <td class="nosort">Premium</td>
              <td class="nosort">Fees Billed</td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="policy in policiesWithMissingPremiumOrFees"
              :key="policy.policyId">
              <td class="nosort">{{ policy.description }}</td>
              <td class="nosort">
                <span v-if="policy.mainPolicyNumber">{{ policy.mainPolicyNumber }}</span>
                <span
                  v-else
                  class="text-gray-400"
                  >&lt;blank&gt;</span
                >
              </td>
              <td class="nosort">{{ formatCurrency(policy.policyPremium, true) }}</td>
              <td class="nosort">{{ formatCurrency(policy.feesBilled, true) }}</td>
            </tr>
          </tbody>
        </table>
      </template>

      <template #actions>
        <AppButton
          primary
          @click="showInvalidMissingPremiumOrFees = false">
          Done
        </AppButton>
      </template>
    </AppModal>

    <AppModal
      :show="showInvalidDates"
      title="Policies with Invalid Dates"
      size="xl"
      action-button-label="Close"
      @close="showInvalidDates = false"
      :handle-cancel="() => (showInvalidDates = false)">
      <template #content>
        <div class="mb-4">
          <h3>Selection Criteria</h3>
          <p>Policies with effective dates before 2010, policy terms longer than 10 years, or effective dates entered far into the future.</p>
        </div>

        <table class="apptable dense --tight-first text-sm">
          <thead>
            <tr>
              <td>Policy Number</td>
              <td>Description</td>
              <td>Effective Date</td>
              <td>Expiration Date</td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="policy in policiesWithInvalidDates"
              :key="policy.policyId">
              <td>
                <span v-if="policy.mainPolicyNumber">{{ policy.mainPolicyNumber }}</span>
                <span
                  v-else
                  class="text-gray-400"
                  >&lt;blank&gt;</span
                >
              </td>
              <td>{{ policy.description }}</td>
              <td>{{ policy.effectiveDate }}</td>
              <td>{{ policy.expirationDate }}</td>
            </tr>
          </tbody>
        </table>
      </template>

      <template #actions>
        <AppButton
          primary
          @click="showInvalidDates = false">
          Done
        </AppButton>
      </template>
    </AppModal>

    <AppModal
      :show="showUnvalidatedBindersModal"
      title="Binders Without Validated Results"
      size="lg"
      action-button-label="Close"
      @close="showUnvalidatedBindersModal = false"
      :handle-cancel="() => (showUnvalidatedBindersModal = false)">
      <template #content>
        <div class="mb-4">
          <h3>Selection Criteria</h3>
          <p>
            Binders that have been processed by AI extraction but have not yet been reviewed and validated. Click the link icon to open the AI Data
            Extraction Results page for a specific binder.
          </p>
        </div>

        <table class="apptable dense --tight-first text-sm">
          <thead>
            <tr>
              <td class="nosort">Policy Number</td>
              <td class="nosort">Description</td>
              <td class="nosort">Document</td>
              <td class="nosort"></td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="binder in unvalidatedBinders"
              :key="binder.promptResultId">
              <td>
                <span v-if="binder.mainPolicyNumber">{{ binder.mainPolicyNumber }}</span>
                <span
                  v-else
                  class="text-gray-400"
                  >&lt;blank&gt;</span
                >
              </td>
              <td>{{ binder.description }}</td>
              <td>{{ binder.documentName }}</td>
              <td>
                <router-link
                  :to="{ name: 'AIVerificationResults', query: { resultId: binder.promptResultId } }"
                  class="hover:text-secondary-focus text-secondary"
                  target="_blank">
                  <ExternalLink :size="16" />
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <template #actions>
        <AppButton
          primary
          @click="showUnvalidatedBindersModal = false">
          Done
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>
